import type { AppShellNavSection } from '@avila-roffe/components-web';
import {
  Avatar,
  AvatarFallback,
  Button,
  AppShell as DesignSystemAppShell,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from '@avila-roffe/components-web';
import { ChevronsUpDown, LogOut, Settings, Upload, UserRound } from 'lucide-react';
import React, { type ReactNode, Suspense, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useNavigation } from '../../hooks/useNavigation';

// ─── Router link adapter ──────────────────────────────────────────────────────

const RouterLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ href, ...props }, ref) => <Link to={href ?? '#'} ref={ref} {...props} />);
RouterLink.displayName = 'RouterLink';

// ─── Logo ─────────────────────────────────────────────────────────────────────

function LogoMark() {
  return (
    <div className="flex items-center gap-2.5 px-1 py-0.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold select-none">
        MR
      </div>
      <span className="text-sm font-semibold text-foreground">MyTaible Restaurant</span>
    </div>
  );
}

// ─── Publish Changes button ───────────────────────────────────────────────────

interface PublishButtonProps {
  enabled?: boolean;
  onPublish?: () => void;
}

function PublishButton({ enabled = false, onPublish }: PublishButtonProps) {
  return (
    <Button
      size="sm"
      disabled={!enabled}
      onClick={onPublish}
      aria-label="Publish customer-facing changes"
    >
      <Upload className="size-4" aria-hidden="true" />
      Publish Changes
    </Button>
  );
}

// ─── User menu (topbar) ───────────────────────────────────────────────────────

function TopbarUserMenu() {
  const { logout } = useAuth();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const initials = currentUser?.initials ?? 'MR';
  const displayName = currentUser?.displayName ?? '…';
  const email = currentUser?.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden sm:block text-sm font-medium text-foreground">{displayName}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" sideOffset={8} className="w-64">
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-foreground leading-none">
              {displayName}
            </span>
            {email && email !== displayName && (
              <span className="truncate text-xs text-muted-foreground leading-none">{email}</span>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/preferences')}>
          <UserRound className="size-4" />
          My Account
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="size-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Nav skeleton (mobile sheet footer placeholder) ───────────────────────────

function NavSkeleton() {
  return (
    <div className="flex flex-col gap-1 px-2 py-2">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full rounded-md" />
      ))}
    </div>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

interface AppShellProps {
  children: ReactNode;
  /** When true the Publish Changes button becomes enabled */
  hasPublishableChanges?: boolean;
  onPublish?: () => void;
}

export function AppShell({ children, hasPublishableChanges = false, onPublish }: AppShellProps) {
  const location = useLocation();
  const currentUser = useCurrentUser();
  const { nav: rawNav, isLoading: navLoading } = useNavigation();

  // Derive active state from current pathname
  const nav: AppShellNavSection[] = useMemo(
    () =>
      rawNav.map((section) => ({
        ...section,
        items: section.items.map((item) => {
          // An item with sub-items is active if any child path matches
          const childActive = item.items?.some(
            (child) =>
              child.href != null &&
              (location.pathname === child.href || location.pathname.startsWith(`${child.href}/`)),
          );
          const selfActive =
            item.href != null &&
            (location.pathname === item.href || location.pathname.startsWith(`${item.href}/`));

          return {
            ...item,
            active: selfActive || childActive,
          };
        }),
      })),
    [rawNav, location.pathname],
  );

  // Topbar right-side slot: Publish Changes + user menu
  const topbarActions = (
    <div className="flex items-center gap-2">
      <PublishButton enabled={hasPublishableChanges} onPublish={onPublish} />
      <TopbarUserMenu />
    </div>
  );

  return (
    <Suspense>
      <DesignSystemAppShell
        layout="app-shell-04"
        logo={<LogoMark />}
        nav={navLoading ? [] : nav}
        mobileNav={navLoading ? [] : nav}
        sidebarFooter={navLoading ? <NavSkeleton /> : null}
        user={{ name: currentUser?.displayName ?? '' }}
        topbar={{ showSearch: false, actions: topbarActions }}
        linkComponent={RouterLink}
        locationKey={location.key}
      >
        <div className="flex flex-col flex-1 overflow-y-auto gap-4 min-h-0 p-4 sm:p-6">
          {children}
        </div>
      </DesignSystemAppShell>
    </Suspense>
  );
}

export default AppShell;
